import { FC, useState } from 'react';
import { Card, CardBody, Icon, Button, Badge } from '@aidkitorg/component-library';
import { useHistory } from 'react-router-dom';
import { useLocalizedStrings } from '../../../../Localization';
import { RelatedCasesSectionProps } from '../shared/types';
import { BadgeVariant } from '@aidkitorg/component-library/dist/types/src/Badge';

/**
 * Related cases section with expandable card showing related support cases
 */
export const RelatedCasesSection: FC<RelatedCasesSectionProps> = ({ relatedCases }) => {
  const L = useLocalizedStrings();
  const history = useHistory();
  const [relatedCasesExpanded, setRelatedCasesExpanded] = useState(false);

  if (relatedCases.length === 0) {
    return null;
  }

  return (
    <div className="min-w-0 space-y-3">
      {/* Yellow collapsible header card */}
      <Button
        variant="unstyled"
        className="bg-yellow-100 rounded-lg border-2 !border-yellow-400 w-full !justify-between"
        onClick={() => setRelatedCasesExpanded(!relatedCasesExpanded)}
        icon={
          <Icon
            role="decorative"
            type="solid"
            name="ChevronDownIcon"
            className={`transition-transform ${relatedCasesExpanded ? "" : "-rotate-90"}`}
          />
        }
        iconPlacement="right"
      >
        <div className="flex items-center gap-1">
          <Icon role="decorative" className="ml-0" type="outline" strokeWidth={1.5} name="ExclamationTriangleIcon" />
          <span className="text-sm">
            {relatedCases.length}{" "}
            {relatedCases.length === 1 ? L.support.related_case_found : L.support.related_cases_found}
          </span>
        </div>
      </Button>

      {/* Expanded related cases */}
      {relatedCasesExpanded && (
        <div className="space-y-3">
          {relatedCases.map((relatedCase) => {
            if (!relatedCase.row.support_case) return null;

            // Get status badge colors
            const variant =
              {
                open: "success",
                resolved: "neutral",
                escalated: "error",
                viewed: "info",
              }[relatedCase.row.support_case_status || "viewed"] as BadgeVariant;

            // Get most recent message
            const lastMessage = relatedCase.row.last_message_text || "No messages";

            return (
              <Card>
                <CardBody className="cursor-pointer">
                  <div
                    key={relatedCase.row.support_case}
                    onClick={() => history.push(`/admin-panel/support/${relatedCase.row.support_case}`)}
                  >
                    <div className="space-y-1">
                      {/* Header row with case ID and status */}
                      <div className="flex items-start justify-between">
                        <div className="text-sm font-semibold">
                          {L.support.case} {relatedCase.row.support_case.split("-")[0]}
                        </div>
                        <Badge className="text-xs" variant={variant}>
                          {relatedCase.row.support_case_status || "open"}
                        </Badge>
                      </div>

                      {/* Assigned to */}
                      <div className="text-xs overflow-hidden">
                        <span className="font-medium">{L.support.assigned}</span>
                        {" "}
                        {relatedCase.row.assigned_to_name || "Unassigned"}
                      </div>

                      {/* Most recent message */}
                      <div className="text-xs break-words overflow-hidden">
                        {lastMessage.length > 100 ? `${lastMessage.slice(0, 100)}...` : lastMessage}
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
